import { useState } from 'react';
import type { Member } from '@/types';
import { roleLabels } from '@/mocks/organization';

interface DeleteMemberModalProps {
  member: Member | null;
  onClose: () => void;
  onConfirm: (memberId: string) => void;
}

export default function DeleteMemberModal({ member, onClose, onConfirm }: DeleteMemberModalProps) {
  const [deleting, setDeleting] = useState(false);

  if (!member) return null;

  const handleConfirm = () => {
    setDeleting(true);
    setTimeout(() => {
      onConfirm(member.id);
      setDeleting(false);
    }, 500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-background-200 w-full max-w-md shadow-sm">
        <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground-900">Удалить участника</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 transition-colors cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-red-500" />
            </div>
            <p className="text-sm text-foreground-700">
              Вы уверены, что хотите удалить{' '}
              <span className="font-medium text-foreground-900">{member.name}</span> из команды?
            </p>
          </div>

          <div className="bg-background-50 rounded-xl p-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-foreground-500">Email</span>
              <span className="text-foreground-700">{member.email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-foreground-500">Текущая роль</span>
              <span className="text-foreground-700">{roleLabels[member.role]}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-foreground-500">Статус</span>
              <span className="text-foreground-700">
                {member.status === 'active' ? 'Активен' : 'Ожидает'}
              </span>
            </div>
          </div>

          <p className="text-xs text-foreground-500">
            Это действие нельзя отменить. Участник потеряет доступ ко всем вакансиям и кандидатам.
          </p>
        </div>

        <div className="px-5 py-4 border-t border-background-100 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Отмена
          </button>
          <button
            onClick={handleConfirm}
            disabled={deleting}
            className="px-4 py-2 rounded-xl text-sm bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            {deleting && <i className="ri-loader-4-line animate-spin" />}
            Удалить
          </button>
        </div>
      </div>
    </div>
  );
}